import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Code, Play, Square, ArrowRight, Database, Wifi, Settings, Package, GitBranch, MessageSquare, Monitor, FileText, Coffee, Book, Zap, Download, Upload, CheckCircle, AlertCircle } from 'lucide-react';

const SDKGuidePage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [expandedSubsections, setExpandedSubsections] = useState({});

  const toggleSubsection = (subsectionId) => {
    setExpandedSubsections(prev => ({
      ...prev,
      [subsectionId]: !prev[subsectionId]
    }));
  };

  const sections = [
    {
      id: 'overview',
      title: 'Vue d\'ensemble',
      icon: Book,
      description: 'Architecture et concepts fondamentaux'
    },
    {
      id: 'interaction',
      title: 'Objet Interaction',
      icon: MessageSquare,
      description: 'Modèle de communication standardisé'
    },
    {
      id: 'flows',
      title: 'Cycles REC/RUN',
      icon: ArrowRight,
      description: 'Flux d\'enregistrement et d\'exécution'
    },
    {
      id: 'implementation',
      title: 'Implémentation',
      icon: Code,
      description: 'Guide de développement complet'
    },
    {
      id: 'java-example',
      title: 'Exemple Java',
      icon: Coffee,
      description: 'Agent Spring Boot complet'
    },
    {
      id: 'python-example',
      title: 'Exemple Python',
      icon: Zap,
      description: 'Agent asyncio websocket'
    },
    {
      id: 'deployment',
      title: 'Déploiement',
      icon: Upload,
      description: 'Configuration et mise en production'
    }
  ];

  const CodeBlock = ({ title, language, children, className = "" }) => (
    <div className={`mb-6 ${className}`}>
      <div className="bg-gray-800 text-gray-200 px-4 py-2 rounded-t-lg text-sm font-medium flex items-center justify-between">
        <span>{title}</span>
        {language && <span className="text-gray-400 text-xs bg-gray-700 px-2 py-1 rounded">{language}</span>}
      </div>
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-b-lg overflow-x-auto text-sm leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );

  const ExpandableSection = ({ id, title, children, defaultExpanded = false }) => {
    const isExpanded = expandedSubsections[id] ?? defaultExpanded;
    
    return (
      <div className="mb-6 border border-gray-200 rounded-lg shadow-sm">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
          onClick={() => toggleSubsection(id)}
        >
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </div>
        {isExpanded && (
          <div className="border-t border-gray-200 p-4">
            {children}
          </div>
        )}
      </div>
    );
  };

  const StatusIndicator = ({ status }) => {
    const statusConfig = {
      'PENDING': { color: 'text-yellow-600', bg: 'bg-yellow-100', icon: AlertCircle },
      'IN_PROGRESS': { color: 'text-blue-600', bg: 'bg-blue-100', icon: Play },
      'COMPLETED': { color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle },
      'FAILED': { color: 'text-red-600', bg: 'bg-red-100', icon: AlertCircle },
      'BLOCKED': { color: 'text-gray-600', bg: 'bg-gray-100', icon: Square },
      'SKIPPED': { color: 'text-gray-600', bg: 'bg-gray-100', icon: ArrowRight }
    };
    
    const config = statusConfig[status] || statusConfig['PENDING'];
    const Icon = config.icon;
    
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${config.bg} ${config.color}`}>
        <Icon className="w-3 h-3" />
        <span>{status}</span>
      </div>
    );
  };

  const InteractionFlow = ({ title, color, steps }) => (
    <div className={`bg-${color}-50 border border-${color}-200 rounded-lg p-6 mb-6`}>
      <h3 className={`text-xl font-semibold text-${color}-900 mb-4`}>{title}</h3>
      <div className="space-y-4">
        {steps.map((step, index) => (
          <div key={index} className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-8 h-8 bg-${color}-100 rounded-full flex items-center justify-center`}>
              <span className={`text-${color}-600 font-semibold text-sm`}>{index + 1}</span>
            </div>
            <div>
              <h4 className={`font-semibold text-${color}-900 mb-1`}>{step.name}</h4>
              <p className={`text-${color}-700 text-sm`}>{step.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Architecture du SDK Automata</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Le SDK Automata permet de créer des agents intelligents capables d'automatiser des tâches complexes 
                via des cycles d'enregistrement (REC) et d'exécution (RUN).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Database className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold text-blue-900">Cycle REC</h3>
                </div>
                <p className="text-blue-800 mb-4">
                  Phase d'enregistrement où l'agent capture les actions utilisateur et l'état du système.
                </p>
                <ul className="space-y-2 text-sm text-blue-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Capture des interactions utilisateur
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Enregistrement de l'état système
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Création de séquences d'actions
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Play className="w-8 h-8 text-green-600" />
                  <h3 className="text-xl font-semibold text-green-900">Cycle RUN</h3>
                </div>
                <p className="text-green-800 mb-4">
                  Phase d'exécution où l'agent reproduit automatiquement les actions enregistrées.
                </p>
                <ul className="space-y-2 text-sm text-green-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Exécution automatique des actions
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Vérification des résultats
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Gestion des erreurs
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Wifi className="w-8 h-8 text-gray-600" />
                <h3 className="text-xl font-semibold text-gray-900">Communication WebSocket</h3>
              </div>
              <p className="text-gray-700 mb-4">
                Les agents communiquent avec le serveur Automata via des connexions WebSocket bidirectionnelles,
                permettant une communication en temps réel et une synchronisation parfaite.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <Upload className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900">Envoi d'Interactions</p>
                    <p className="text-xs text-gray-600">Agent → Serveur</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <Download className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900">Réception d'Ordres</p>
                    <p className="text-xs text-gray-600">Serveur → Agent</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <ArrowRight className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900">Synchronisation</p>
                    <p className="text-xs text-gray-600">Temps réel</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'interaction':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Objet Interaction</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                L'objet Interaction est l'unité de base de communication dans le système Automata. 
                Il standardise tous les échanges entre les agents et le serveur.
              </p>
            </div>

            <ExpandableSection id="interaction-structure" title="Structure de l'Interaction" defaultExpanded={true}>
              <CodeBlock title="Modèle Interaction (JSON)" language="json">
{`{
  "interactionId": "string",
  "sequence": "integer",
  "timestamp": "long",
  "direction": "REQUEST|RESPONSE",
  "type": "InteractionType",
  "status": "InteractionStatus",
  "technicalProperties": {
    "key": "value"
  },
  "variableMappings": {
    "variable": "value"
  }
}`}
              </CodeBlock>

              <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Propriétés essentielles</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li><strong>interactionId:</strong> Identifiant unique de l'interaction</li>
                    <li><strong>sequence:</strong> Numéro de séquence pour l'ordonnancement</li>
                    <li><strong>timestamp:</strong> Horodatage en millisecondes Unix</li>
                    <li><strong>direction:</strong> Sens de l'interaction (REQUEST/RESPONSE)</li>
                    <li><strong>type:</strong> Type d'interaction (REC_*, RUN_*)</li>
                    <li><strong>status:</strong> État de l'interaction</li>
                  </ul>
                </div>
              </div>
            </ExpandableSection>

            <ExpandableSection id="interaction-types" title="Types d'Interactions">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-semibold text-green-700 mb-4">Types REC (Enregistrement)</h3>
                  <div className="space-y-3">
                    {[
                      { type: 'REC_START', desc: 'Démarrage de l\'enregistrement' },
                      { type: 'REC_ACTION', desc: 'Action utilisateur capturée' },
                      { type: 'REC_STOP', desc: 'Arrêt de l\'enregistrement' },
                      { type: 'REC_STATE', desc: 'État actuel de l\'application' },
                      { type: 'REC_ERROR', desc: 'Erreur pendant l\'enregistrement' }
                    ].map((item, index) => (
                      <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <code className="text-green-800 font-mono text-sm font-semibold">{item.type}</code>
                        <p className="text-green-700 text-sm mt-1">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-blue-700 mb-4">Types RUN (Exécution)</h3>
                  <div className="space-y-3">
                    {[
                      { type: 'RUN_START', desc: 'Démarrage de l\'exécution d\'un scénario' },
                      { type: 'RUN_ACTION', desc: 'Action à exécuter' },
                      { type: 'RUN_STOP', desc: 'Arrêt de l\'exécution' },
                      { type: 'RUN_STATE', desc: 'État pendant l\'exécution' },
                      { type: 'RUN_ACTION_EXECUTED', desc: 'Confirmation d\'exécution d\'action' },
                      { type: 'RUN_COMPLETE', desc: 'Exécution terminée' },
                      { type: 'RUN_ERROR', desc: 'Erreur pendant l\'exécution' }
                    ].map((item, index) => (
                      <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <code className="text-blue-800 font-mono text-sm font-semibold">{item.type}</code>
                        <p className="text-blue-700 text-sm mt-1">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ExpandableSection>

            <ExpandableSection id="interaction-status" title="États des Interactions">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { status: 'PENDING', desc: 'En attente de traitement' },
                  { status: 'IN_PROGRESS', desc: 'En cours de traitement' },
                  { status: 'COMPLETED', desc: 'Terminé avec succès' },
                  { status: 'FAILED', desc: 'Échec du traitement' },
                  { status: 'BLOCKED', desc: 'Bloqué par une condition' },
                  { status: 'SKIPPED', desc: 'Ignoré volontairement' }
                ].map((item, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <StatusIndicator status={item.status} />
                    </div>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </ExpandableSection>
          </div>
        );

      case 'flows':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Cycles REC/RUN</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Les cycles REC et RUN forment le cœur du système Automata. Ils permettent d'enregistrer 
                des séquences d'actions puis de les reproduire automatiquement.
              </p>
            </div>

            <InteractionFlow
              title="Cycle REC - Enregistrement d'interactions"
              color="green"
              steps={[
                { 
                  name: 'REC_START', 
                  description: 'Le backend initie un enregistrement. L\'agent confirme et se prépare à capturer les actions utilisateur sur l\'application cible.' 
                },
                { 
                  name: 'Capture Active', 
                  description: 'L\'agent surveille l\'application et capture chaque action utilisateur : clics, saisies, navigation, etc.' 
                },
                { 
                  name: 'REC_ACTION', 
                  description: 'Chaque action capturée est immédiatement envoyée au backend avec tous les détails techniques nécessaires.' 
                },
                { 
                  name: 'REC_STATE', 
                  description: 'L\'agent envoie périodiquement l\'état de l\'application pour permettre la synchronisation.' 
                },
                { 
                  name: 'REC_STOP', 
                  description: 'L\'enregistrement se termine sur demande du backend ou automatiquement. Le scénario est sauvegardé.' 
                }
              ]}
            />

            <InteractionFlow
              title="Cycle RUN - Exécution de scénarios"
              color="blue"
              steps={[
                { 
                  name: 'RUN_START', 
                  description: 'Le backend demande l\'exécution d\'un scénario existant avec les paramètres d\'environnement. L\'agent confirme et initialise l\'exécution.' 
                },
                { 
                  name: 'Préparation', 
                  description: 'L\'agent se connecte à l\'application, met à jour la base de données et prépare l\'environnement d\'exécution selon le scénario.' 
                },
                { 
                  name: 'RUN_ACTION', 
                  description: 'Le backend envoie séquentiellement chaque action du scénario. L\'agent les traite dans l\'ordre et dans des threads dédiés.' 
                },
                { 
                  name: 'RUN_ACTION_EXECUTED', 
                  description: 'Pour chaque action traitée, l\'agent confirme son exécution avec les résultats obtenus (succès, échec, données récupérées).' 
                },
                { 
                  name: 'RUN_COMPLETE', 
                  description: 'Une fois toutes les actions exécutées, l\'agent envoie un rapport final avec l\'état de l\'exécution et les résultats globaux.' 
                }
              ]}
            />

            <ExpandableSection id="rec-flow" title="Flux REC - Enregistrement détaillé">
              <CodeBlock title="Exemple de séquence REC" language="json">
{`[
  {
    "interactionId": "REC_001",
    "type": "REC_START",
    "direction": "REQUEST",
    "status": "COMPLETED",
    "technicalProperties": {
      "mode": "MANUAL",
      "target": "web_application"
    }
  },
  {
    "interactionId": "REC_002",
    "type": "REC_ACTION",
    "direction": "REQUEST",
    "status": "COMPLETED",
    "technicalProperties": {
      "action": "click",
      "element": "#login-button",
      "coordinates": {"x": 150, "y": 50}
    }
  },
  {
    "interactionId": "REC_003",
    "type": "REC_STATE",
    "direction": "RESPONSE",
    "status": "COMPLETED",
    "technicalProperties": {
      "url": "https://app.example.com/dashboard",
      "title": "Dashboard",
      "elements": ["nav", "main", "footer"]
    }
  }
]`}
              </CodeBlock>
            </ExpandableSection>
          </div>
        );

      case 'implementation':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Guide d'Implémentation</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Ce guide vous accompagne dans la création d'agents Automata robustes et performants.
              </p>
            </div>

            <ExpandableSection id="architecture" title="Architecture Recommandée" defaultExpanded={true}>
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-purple-900 mb-4">Principes d'Architecture</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4">
                      <Settings className="w-8 h-8 text-purple-600 mb-2" />
                      <h4 className="font-semibold text-gray-900 mb-2">Modularité</h4>
                      <p className="text-sm text-gray-600">Séparation claire des responsabilités</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <GitBranch className="w-8 h-8 text-purple-600 mb-2" />
                      <h4 className="font-semibold text-gray-900 mb-2">Évolutivité</h4>
                      <p className="text-sm text-gray-600">Architecture extensible et maintenable</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <Package className="w-8 h-8 text-purple-600 mb-2" />
                      <h4 className="font-semibold text-gray-900 mb-2">Réutilisabilité</h4>
                      <p className="text-sm text-gray-600">Composants réutilisables et configurables</p>
                    </div>
                  </div>
                </div>

                <CodeBlock title="Structure de projet recommandée">
{`automata-agent/
├── config/
│   ├── application.properties
│   └── websocket-config.json
├── src/
│   ├── main/
│   │   ├── java/
│   │   │   └── com/
│   │   │       └── automata/
│   │   │           └── agent/
│   │   │               ├── AutomataAgent.java
│   │   │               ├── config/
│   │   │               ├── handlers/
│   │   │               ├── models/
│   │   │               └── services/
│   │   └── resources/
│   │       └── application.yml
└── pom.xml`}
                </CodeBlock>
              </div>
            </ExpandableSection>

            <ExpandableSection id="websocket-implementation" title="Implémentation WebSocket">
              <CodeBlock title="Configuration WebSocket Spring Boot" language="java">
{`@Configuration
@EnableWebSocket
public class WebSocketConfig implements WebSocketConfigurer {

    @Override
    public void registerWebSocketHandlers(WebSocketHandlerRegistry registry) {
        registry.addHandler(new RecTaskHandler(), "/RecTask")
                .addHandler(new RunTaskHandler(), "/RunTask")
                .setAllowedOrigins("*")
                .withSockJS();
    }
}`}
              </CodeBlock>
            </ExpandableSection>
          </div>
        );

      case 'java-example':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Exemple Complet Java</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Implémentation complète d'un agent Automata en Java avec Spring Boot et Selenium WebDriver.
              </p>
            </div>

            <ExpandableSection id="java-dependencies" title="Dépendances Maven" defaultExpanded={true}>
              <CodeBlock title="pom.xml" language="xml">
{`<?xml version="1.0" encoding="UTF-8"?>
<project xmlns="http://maven.apache.org/POM/4.0.0">
    <modelVersion>4.0.0</modelVersion>
    
    <groupId>com.automata</groupId>
    <artifactId>automata-web-agent</artifactId>
    <version>1.0.0</version>
    <packaging>jar</packaging>

    <parent>
        <groupId>org.springframework.boot</groupId>
        <artifactId>spring-boot-starter-parent</artifactId>
        <version>3.1.0</version>
        <relativePath/>
    </parent>

    <dependencies>
        <dependency>
            <groupId>org.springframework.boot</groupId>
            <artifactId>spring-boot-starter-websocket</artifactId>
        </dependency>
        <dependency>
            <groupId>org.selenium</groupId>
            <artifactId>selenium-java</artifactId>
            <version>4.10.0</version>
        </dependency>
    </dependencies>
</project>`}
              </CodeBlock>
            </ExpandableSection>

            <ExpandableSection id="java-interaction-model" title="Modèle Interaction">
              <CodeBlock title="Interaction.java" language="java">
{`package com.automata.agent.models;

import com.fasterxml.jackson.annotation.JsonProperty;
import java.time.Instant;
import java.util.HashMap;
import java.util.Map;

public class Interaction {
    
    @JsonProperty("interactionId")
    private String interactionId;
    
    @JsonProperty("sequence")
    private Integer sequence;
    
    @JsonProperty("timestamp")
    private Long timestamp;
    
    @JsonProperty("direction")
    private Direction direction;
    
    @JsonProperty("type")
    private InteractionType type;
    
    @JsonProperty("status")
    private InteractionStatus status;
    
    @JsonProperty("technicalProperties")
    private Map<String, Object> technicalProperties = new HashMap<>();
    
    public enum Direction {
        REQUEST, RESPONSE
    }
    
    public enum InteractionType {
        REC_START, REC_ACTION, REC_STOP, REC_STATE, REC_ERROR,
        RUN_START, RUN_ACTION, RUN_STOP, RUN_STATE, 
        RUN_ACTION_EXECUTED, RUN_COMPLETE, RUN_ERROR
    }
    
    public enum InteractionStatus {
        PENDING, IN_PROGRESS, COMPLETED, FAILED, BLOCKED, SKIPPED
    }
    
    // Constructeurs et getters/setters...
}`}
              </CodeBlock>
            </ExpandableSection>
          </div>
        );

      case 'python-example':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Exemple Complet Python</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Implémentation complète d'un agent Automata en Python avec asyncio et websockets.
              </p>
            </div>

            <ExpandableSection id="python-structure" title="Structure du projet Python" defaultExpanded={true}>
              <CodeBlock title="Structure recommandée">
{`automata-agent-api/
├── requirements.txt
├── config.json
├── main.py
├── src/
│   ├── __init__.py
│   ├── config/
│   │   ├── __init__.py
│   │   └── config_loader.py
│   ├── models/
│   │   ├── __init__.py
│   │   └── interaction.py
│   ├── services/
│   │   ├── __init__.py
│   │   ├── base_handler.py
│   │   ├── rec_api_task.py
│   │   └── run_api_task.py
│   └── utils/
│       ├── __init__.py
│       └── logger.py
└── README.md`}
              </CodeBlock>

              <CodeBlock title="requirements.txt" language="txt">
{`websockets==11.0.3
aiohttp==3.8.5
asyncio==3.4.3
json-logging==1.3.0
requests==2.31.0
python-dotenv==1.0.0`}
              </CodeBlock>
            </ExpandableSection>

            <ExpandableSection id="python-main" title="Application principale">
              <CodeBlock title="main.py" language="python">
{`import asyncio
import websockets
import json
import logging
from src.config.config_loader import ConfigLoader
from src.services.rec_api_task import RecApiTask
from src.services.run_api_task import RunApiTask
from src.utils.logger import setup_logger

class AutomataApiAgent:
    def __init__(self):
        self.config = ConfigLoader()
        self.logger = setup_logger(__name__)
        
        # Services
        self.rec_service = RecApiTask(self.config)
        self.run_service = RunApiTask(self.config)
        
    async def handle_websocket(self, websocket, path):
        """Gestionnaire principal des connexions WebSocket"""
        session_id = id(websocket)
        
        # Déterminer le service selon le path
        if path == "/RecApiTask":
            service = self.rec_service
        elif path == "/RunApiTask":
            service = self.run_service
        else:
            await websocket.close(code=1002, reason="Path non supporté")
            return
        
        try:
            await service.initialize_session(session_id, websocket)
            
            async for message in websocket:
                await service.handle_message(session_id, websocket, message)
                    
        except websockets.exceptions.ConnectionClosed:
            self.logger.info(f"Connexion fermée: {session_id}")
        finally:
            await service.cleanup_session(session_id)
    
    async def start_server(self):
        """Démarrer le serveur WebSocket"""
        host = self.config.get("HOST", "0.0.0.0")
        port = self.config.get("SERVER_PORT", 8083)
        
        async with websockets.serve(self.handle_websocket, host, port):
            self.logger.info(f"Serveur démarré sur {host}:{port}")
            await asyncio.Future()  # run forever

if __name__ == "__main__":
    agent = AutomataApiAgent()
    asyncio.run(agent.start_server())`}
              </CodeBlock>
            </ExpandableSection>

            <ExpandableSection id="python-models" title="Modèles Python">
              <CodeBlock title="src/models/interaction.py" language="python">
{`from enum import Enum
from datetime import datetime
from typing import Dict, Any, Optional
import json

class Direction(Enum):
    REQUEST = "REQUEST"
    RESPONSE = "RESPONSE"

class InteractionStatus(Enum):
    PENDING = "PENDING"
    IN_PROGRESS = "IN_PROGRESS"
    COMPLETED = "COMPLETED"
    FAILED = "FAILED"
    BLOCKED = "BLOCKED"
    SKIPPED = "SKIPPED"

class InteractionType(Enum):
    # Types REC
    REC_START = "REC_START"
    REC_ACTION = "REC_ACTION"
    REC_STOP = "REC_STOP"
    REC_STATE = "REC_STATE"
    REC_ERROR = "REC_ERROR"
    
    # Types RUN
    RUN_START = "RUN_START"
    RUN_ACTION = "RUN_ACTION"
    RUN_STOP = "RUN_STOP"
    RUN_STATE = "RUN_STATE"
    RUN_ACTION_EXECUTED = "RUN_ACTION_EXECUTED"
    RUN_COMPLETE = "RUN_COMPLETE"
    RUN_ERROR = "RUN_ERROR"

class Interaction:
    def __init__(self):
        self.interaction_id: Optional[str] = None
        self.sequence: Optional[int] = None
        self.timestamp: datetime = datetime.now()
        self.direction: Optional[Direction] = None
        self.type: Optional[InteractionType] = None
        self.status: InteractionStatus = InteractionStatus.PENDING
        self.technical_properties: Dict[str, Any] = {}
        self.variable_mappings: Dict[str, Any] = {}
    
    def to_json(self) -> str:
        """Convertir l'Interaction en JSON"""
        data = {
            'interactionId': self.interaction_id,
            'sequence': self.sequence,
            'timestamp': int(self.timestamp.timestamp() * 1000),
            'direction': self.direction.value if self.direction else None,
            'type': self.type.value if self.type else None,
            'status': self.status.value,
            'technicalProperties': self.technical_properties,
            'variableMappings': self.variable_mappings
        }
        
        return json.dumps(data, ensure_ascii=False)`}
              </CodeBlock>
            </ExpandableSection>
          </div>
        );

      case 'deployment':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Déploiement et Production</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Guide complet pour déployer vos agents Automata en production.
              </p>
            </div>

            <ExpandableSection id="docker-deployment" title="Déploiement Docker" defaultExpanded={true}>
              <CodeBlock title="Dockerfile Java" language="dockerfile">
{`FROM openjdk:17-jdk-slim

# Installer Chrome pour Selenium
RUN apt-get update && apt-get install -y \\
    wget \\
    gnupg \\
    unzip \\
    && wget -q -O - https://dl-ssl.google.com/linux/linux_signing_key.pub | apt-key add - \\
    && echo "deb [arch=amd64] http://dl.google.com/linux/chrome/deb/ stable main" >> /etc/apt/sources.list.d/google.list \\
    && apt-get update \\
    && apt-get install -y google-chrome-stable \\
    && rm -rf /var/lib/apt/lists/*

WORKDIR /app
COPY target/automata-web-agent-1.0.0.jar app.jar
EXPOSE 8080
ENTRYPOINT ["java", "-jar", "app.jar"]`}
              </CodeBlock>

              <CodeBlock title="docker-compose.yml" language="yaml">
{`version: '3.8'

services:
  automata-java-agent:
    build: .
    ports:
      - "8080:8080"
    environment:
      - SPRING_PROFILES_ACTIVE=production
      - AUTOMATA_SELENIUM_HEADLESS=true
    volumes:
      - ./logs:/app/logs
      - ./config:/app/config
    restart: unless-stopped

  automata-python-agent:
    build: ./python-agent
    ports:
      - "8083:8083"
    environment:
      - PYTHONUNBUFFERED=1
    volumes:
      - ./python-agent/config.json:/app/config.json
      - ./python-agent/logs:/app/logs
    restart: unless-stopped`}
              </CodeBlock>
            </ExpandableSection>

            <ExpandableSection id="kubernetes-deployment" title="Déploiement Kubernetes">
              <CodeBlock title="deployment.yaml" language="yaml">
{`apiVersion: apps/v1
kind: Deployment
metadata:
  name: automata-agent
  labels:
    app: automata-agent
spec:
  replicas: 3
  selector:
    matchLabels:
      app: automata-agent
  template:
    metadata:
      labels:
        app: automata-agent
    spec:
      containers:
      - name: automata-agent
        image: automata/web-agent:latest
        ports:
        - containerPort: 8080
        env:
        - name: SPRING_PROFILES_ACTIVE
          value: "production"
        resources:
          requests:
            memory: "512Mi"
            cpu: "250m"
          limits:
            memory: "1Gi"
            cpu: "500m"
        livenessProbe:
          httpGet:
            path: /health
            port: 8080
          initialDelaySeconds: 30
          periodSeconds: 10
---
apiVersion: v1
kind: Service
metadata:
  name: automata-agent-service
spec:
  selector:
    app: automata-agent
  ports:
  - protocol: TCP
    port: 80
    targetPort: 8080
  type: LoadBalancer`}
              </CodeBlock>
            </ExpandableSection>

            <ExpandableSection id="monitoring" title="Monitoring et Alertes">
              <CodeBlock title="Configuration Prometheus" language="yaml">
{`global:
  scrape_interval: 15s

scrape_configs:
  - job_name: 'automata-agents'
    static_configs:
      - targets: ['automata-agent:8080']
    metrics_path: /actuator/prometheus
    scrape_interval: 5s

rule_files:
  - "alert_rules.yml"

alerting:
  alertmanagers:
    - static_configs:
        - targets:
          - alertmanager:9093`}
              </CodeBlock>
            </ExpandableSection>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Section en cours de développement</h2>
            <p className="text-gray-600">Cette section sera disponible prochainement.</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Code className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">SDK Automata</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Guide de développement</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Sections</h2>
              <nav className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-3" />
                      <div className="text-left">
                        <div className="font-medium">{section.title}</div>
                        <div className="text-xs text-gray-500">{section.description}</div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm p-8">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SDKGuidePage;